import { ReactNode, useState } from 'react';

import { CircleAlertIcon, ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

type Props = {
  messageCount: number;
  isSelected: boolean;
  hasError?: boolean;
  isLoading?: boolean;
  animateIn?: boolean;
  children?: ReactNode;
};

/** Redesigned answer block: a meta row above the answer card, or the error card. */
export const TurnAnswer = ({
  messageCount,
  isSelected,
  hasError = false,
  isLoading = false,
  animateIn = false,
  children,
}: Props) => {
  const { t } = useTranslation();

  const [popIn] = useState(animateIn);

  return (
    <div
      className={cn('flex flex-col items-start gap-1.5 self-stretch pr-6', popIn && 'turn-pop-in')}
    >
      <div className="turn-answer-meta flex h-4 items-center gap-1 self-stretch px-0.5 text-xs leading-4 font-normal text-foreground-muted">
        <ZapIcon size={12} className="shrink-0" />
        <span>·</span>
        {isLoading ? (
          <Skeleton className="h-3 w-24 border border-stroke" />
        ) : (
          <span>{t('try_it.answer_message_count', { count: messageCount })}</span>
        )}
      </div>

      {hasError && (
        <div className="turn-card flex items-start gap-2 self-stretch rounded-lg border border-stroke-destructive bg-background p-3">
          <CircleAlertIcon size={12} className="mt-1 shrink-0 text-destructive" />
          <p className="max-w-md self-stretch text-sm leading-5 font-normal text-foreground">
            {t('try_it.answer_internal_error')}
          </p>
        </div>
      )}

      {!hasError && (
        <div
          className={cn(
            'flex w-full flex-col gap-2',
            // Every response is its own card, wherever the legacy renderers nest it.
            '[&_.turn-card]:rounded-lg [&_.turn-card]:rounded-tl-sm [&_.turn-card]:border [&_.turn-card]:border-stroke [&_.turn-card]:px-3 [&_.turn-card]:pt-3 [&_.turn-card]:pb-4',
            '[&_.response:not(:last-child)]:mb-0 [&_:has(>.turn-card)]:gap-2',
            // `group` is the turn row in `MessageNode`, so the cards follow its hover.
            isSelected
              ? '[&_.turn-card]:bg-background'
              : '[&_.turn-card]:bg-bg-muted-50 group-hover:[&_.turn-card]:bg-background',
            !isSelected &&
              '[&_[data-amend-trigger]]:invisible group-hover:[&_[data-amend-trigger]]:visible'
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
};
